// AiMessageActions.jsx

import React, { useCallback, useMemo } from 'react';

// Routing
import { useNavigate } from 'react-router-dom';

// Utilities
import { buildChannelDeepLinkPath } from 'spaces/utils/buildChannelDeepLinkPath';
import {
  resolveAutomationSavedAction,
  resolveConversationOpenAction,
  resolveDatabaseViewAction,
  resolvePickRecordAction,
  resolveRecordOpenAction,
  resolveRecordOpenNode,
} from 'spaces/utils/aiMessageActions';

// Hooks
import useAiCollectionSettingsModal from 'spaces/hooks/useAiCollectionSettingsModal';
import useDatabaseOpenIntent from 'views/hooks/useDatabaseOpenIntent';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// Components
import Button from 'components/button/Button';
import DatabaseModal from 'collection/settings/DatabaseModal';
import { SETTINGS_TAB_AUTOMATIONS } from 'collection/settings/databaseSettingsTabs';
import AiDisambiguationOptions from 'spaces/components/AiDisambiguationOptions';

function permalinkLabelForUrl(url) {
  const href = String(url || '').trim();
  if (!href) return 'Link';
  try {
    const parsed = new URL(href);
    return parsed.hostname.replace(/^www\./, '') || 'Link';
  } catch {
    return 'Link';
  }
}

const AiMessageActions = React.memo(({
  message,
  surfaceNodes = [],
  disambiguationEnabled = true,
  onSelectDisambiguationOption = null,
}) => {
  // ----- ROUTING ----- //

  const navigate = useNavigate();
  // ----- MEMOS ----- //

  const automationSavedAction = useMemo(
    () => resolveAutomationSavedAction(message),
    [message],
  );
  const pickRecordAction = useMemo(
    () => resolvePickRecordAction(message),
    [message],
  );
  const databaseViewAction = useMemo(
    () => {
      if (automationSavedAction?.databaseView) {
        return automationSavedAction.databaseView;
      }

      const feedDatabaseAction = message?.feed?.databaseAction;
      if (feedDatabaseAction && typeof feedDatabaseAction === 'object') {
        return resolveDatabaseViewAction({ action: feedDatabaseAction });
      }

      return resolveDatabaseViewAction(message);
    },
    [automationSavedAction, message],
  );
  const recordOpenAction = useMemo(
    () => resolveRecordOpenAction(message),
    [message],
  );
  const conversationOpenAction = useMemo(
    () => resolveConversationOpenAction(message),
    [message],
  );
  const recordOpenNode = useMemo(
    () => resolveRecordOpenNode(message, surfaceNodes),
    [message, surfaceNodes],
  );
  // ----- HOOKS ----- //

  const { openNode } = useNodeOpenIntent();
  const { openDatabase } = useDatabaseOpenIntent();
  const {
    canConfigureTypeKey,
    databaseModalProps,
    isOpening,
    openDatabaseSettings,
  } = useAiCollectionSettingsModal();

  const isOpenRecordSelect = pickRecordAction?.selectMode === 'open_record';
  const canHandlePickRecord = isOpenRecordSelect
    || typeof onSelectDisambiguationOption === 'function';

  // ----- HANDLERS ----- //

  /**
   * handlePickRecordOption
   * Opens peek for browse-style picks; otherwise resumes the pending AI intent.
   * @param {object} option - Selected disambiguation option
   * @returns {Promise<void>}
   */
  const handlePickRecordOption = useCallback(async (option = {}) => {
    if (isOpenRecordSelect) {
      const nodeKey = String(option?.nodeKey || '').trim();
      if (!nodeKey) return;
      openNode({
        key: nodeKey,
        title: option?.title || undefined,
        type: option?.typeKey || undefined,
      }, { preferPeek: true });
      return;
    }

    if (typeof onSelectDisambiguationOption === 'function') {
      await onSelectDisambiguationOption(option);
    }
  }, [isOpenRecordSelect, onSelectDisambiguationOption, openNode]);

  const showConfigureButton = Boolean(
    databaseViewAction
    && !automationSavedAction
    && canConfigureTypeKey(databaseViewAction.typeKey),
  );
  const showAutomationSettingsButton = Boolean(
    automationSavedAction
    && canConfigureTypeKey(automationSavedAction.typeKey),
  );

  const hasStandardActions = Boolean(
    databaseViewAction || recordOpenAction || conversationOpenAction || automationSavedAction,
  );

  if (!hasStandardActions && !pickRecordAction) {
    return null;
  }

  // ----- RENDER ----- //

  return (
    <>
      {pickRecordAction ? (
        <AiDisambiguationOptions
          options={pickRecordAction.options}
          enabled={disambiguationEnabled && canHandlePickRecord}
          lockOnSelect={!isOpenRecordSelect}
          onSelect={handlePickRecordOption}
        />
      ) : null}

      {hasStandardActions ? (
        <div className="ai-message-row__action">
          {conversationOpenAction ? (
            <Button
              label={conversationOpenAction.label}
              iconKey="chat"
              width="fit-content"
              onClick={() => {
                const path = buildChannelDeepLinkPath({
                  channelKey: conversationOpenAction.channelKey,
                  baseKey: conversationOpenAction.baseKey,
                  messageKey: conversationOpenAction.messageKey,
                  threadRootKey: conversationOpenAction.threadRootKey,
                });
                if (path) navigate(path);
              }}
            />
          ) : null}
          {recordOpenAction ? (
            <Button
              label={recordOpenAction.label}
              iconKey="open_in_new"
              width="fit-content"
              onClick={() => {
                if (recordOpenNode) {
                  openNode(recordOpenNode);
                }
              }}
            />
          ) : null}
          {recordOpenAction?.permalink ? (
            <Button
              label={permalinkLabelForUrl(recordOpenAction.permalink)}
              iconKey="open_in_new"
              width="fit-content"
              onClick={() => {
                const href = String(recordOpenAction.permalink || '').trim();
                if (!href) return;
                window.open(href, '_blank', 'noopener,noreferrer');
              }}
            />
          ) : null}
          {databaseViewAction ? (
            <Button
              label={databaseViewAction.label}
              iconKey="visibility"
              width="fit-content"
              onClick={() => {
                openDatabase(databaseViewAction.databaseKey, {
                  typeKey: databaseViewAction.typeKey,
                  baseKey: databaseViewAction.baseKey,
                });
              }}
            />
          ) : null}
          {showConfigureButton ? (
            <Button
              disabled={isOpening}
              iconKey="settings"
              label="Configure"
              width="fit-content"
              onClick={() => {
                openDatabaseSettings({
                  databaseKey: databaseViewAction.databaseKey,
                  typeKey: databaseViewAction.typeKey,
                  baseKey: databaseViewAction.baseKey,
                });
              }}
            />
          ) : null}
          {showAutomationSettingsButton ? (
            <Button
              disabled={isOpening}
              iconKey="settings"
              label={automationSavedAction.label}
              width="fit-content"
              onClick={() => {
                openDatabaseSettings({
                  databaseKey: automationSavedAction.databaseKey,
                  typeKey: automationSavedAction.typeKey,
                  baseKey: automationSavedAction.baseKey,
                  initialTab: SETTINGS_TAB_AUTOMATIONS,
                });
              }}
            />
          ) : null}
        </div>
      ) : null}

      {(databaseViewAction || automationSavedAction) ? (
        <DatabaseModal {...databaseModalProps} />
      ) : null}
    </>
  );
});

// Dev Tools
AiMessageActions.displayName = 'AiMessageActions';

export default AiMessageActions;
